/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';
import { PARAMS } from './PARAMS';
import { RedactedToken } from './RedactedToken';

const styles = cssMap({
	promptText: {
		// white on dark-blue bubble in light mode; near-black on lighter-blue bubble in dark mode
		color: token('color.text.inverse'),
		whiteSpace: 'normal',
		wordBreak: 'break-word',
	},
	promptTextOnLight: {
		// for use on neutral/white backgrounds (e.g. assistant response redaction)
		color: token('color.text'),
		whiteSpace: 'normal',
		wordBreak: 'break-word',
	},
	paragraph: {
		margin: '0',
		padding: '0',
	},
});

// Backend may send either [REDACTED] or [REDACT] as the redaction placeholder.
const REDACTED_TOKEN_PATTERN = /(\[REDACTED\]|\[REDACT\])/g;
type RedactedPart = { kind: 'scribble'; delay: number } | { kind: 'text'; text: string };

/**
 * Renders the prompt text with animated scribble overlays for each [REDACTED] / [REDACT] token,
 * preserving the original line-break structure of the message.
 * Multiple scribble spans cascade in with a gap of PARAMS.cascadeOffset ms between each.
 * The enforcement row (GuardIcon + label) is rendered at the top, matching the guard block layout.
 */
export const RedactedPromptContent = ({
	redactedPrompt,
	background = 'dark',
}: {
	redactedPrompt: string;
	background?: 'dark' | 'light';
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const ariaLabel = formatMessage(messages.redactedAriaLabel);

	const processedLines = useMemo(() => {
		let tokenIndex = 0;
		return redactedPrompt.split('\n').map((line) =>
			line.split(REDACTED_TOKEN_PATTERN).map((part): RedactedPart => {
				if (/^\[REDACT(ED)?\]$/.test(part)) {
					const delay = PARAMS.delay + tokenIndex * (PARAMS.duration + PARAMS.cascadeOffset);
					tokenIndex++;
					return { kind: 'scribble', delay };
				}
				return { kind: 'text', text: part };
			}),
		);
	}, [redactedPrompt]);

	return (
		<Box xcss={background === 'light' ? styles.promptTextOnLight : styles.promptText}>
			{processedLines.map((parts, lineIdx) => (
				<Box key={lineIdx} as="p" xcss={styles.paragraph}>
					{parts.map((part, partIdx) =>
						part.kind === 'scribble' ? (
							<RedactedToken key={partIdx} ariaLabel={ariaLabel} delay={part.delay} />
						) : (
							part.text && <span key={partIdx}>{part.text}</span>
						),
					)}
				</Box>
			))}
		</Box>
	);
};
